import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout/Layout';
import { CheckIcon, ChevronRightIcon, UsersIcon, BriefcaseIcon } from 'lucide-react';
export const OnboardingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [userType, setUserType] = useState<string | null>(null);
  const userTypes = [{
    id: 'farmer',
    title: 'Farmer',
    icon: <div size={32} className="text-leaf-green mb-3" data-id="element-933" />,
    description: 'I own or manage agricultural land and am looking for funding or partnerships'
  }, {
    id: 'investor',
    title: 'Investor',
    icon: <BriefcaseIcon size={32} className="text-leaf-green mb-3" data-id="element-934" />,
    description: 'I want to invest in agricultural projects and connect with farmers'
  }, {
    id: 'organization',
    title: 'Organization',
    icon: <UsersIcon size={32} className="text-leaf-green mb-3" data-id="element-935" />,
    description: 'I represent an agricultural organization, cooperative, or NGO'
  }];
  const handleContinue = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      navigate('/projects');
    }
  };
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <div className="w-full max-w-xl mx-auto" data-id="element-936">
            <h2 className="text-2xl font-bold text-center mb-6" data-id="element-937">
              Welcome to GrowConnect
            </h2>
            <p className="text-center text-gray-600 mb-8" data-id="element-938">
              Tell us about yourself so we can personalize your experience
            </p>
            <div className="grid grid-cols-1 gap-4" data-id="element-939">
              {userTypes.map(type => <div key={type.id} className={`card p-6 cursor-pointer transition-all hover:border-leaf-green ${userType === type.id ? 'border-2 border-leaf-green bg-leaf-green/5' : 'border border-gray-200'}`} onClick={() => setUserType(type.id)} data-id="element-940">
                  <div className="flex items-start" data-id="element-941">
                    <div className="flex-1" data-id="element-942">
                      {type.icon}
                      <h3 className="text-xl font-semibold mb-2" data-id="element-943">
                        {type.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-944">{type.description}</p>
                    </div>
                    {userType === type.id && <div className="bg-leaf-green rounded-full p-1" data-id="element-945">
                        <CheckIcon size={20} className="text-white" data-id="element-946" />
                      </div>}
                  </div>
                </div>)}
            </div>
          </div>;
      case 2:
        return <div className="w-full max-w-xl mx-auto" data-id="element-947">
            <h2 className="text-2xl font-bold text-center mb-6" data-id="element-948">
              Complete Your Profile
            </h2>
            <p className="text-center text-gray-600 mb-8" data-id="element-949">
              Tell us more about yourself to help us connect you with the right
              opportunities
            </p>
            <form className="space-y-6" data-id="element-950">
              <div data-id="element-951">
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2" data-id="element-952">
                  Full Name
                </label>
                <input type="text" id="name" className="input-field" placeholder="Enter your full name" required data-id="element-953" />
              </div>
              <div data-id="element-954">
                <label htmlFor="email" className="block text-gray-700 font-medium mb-2" data-id="element-955">
                  Email Address
                </label>
                <input type="email" id="email" className="input-field" placeholder="Enter your email address" required data-id="element-956" />
              </div>
              <div data-id="element-957">
                <label htmlFor="location" className="block text-gray-700 font-medium mb-2" data-id="element-958">
                  Location
                </label>
                <input type="text" id="location" className="input-field" placeholder="City, State, Country" required data-id="element-959" />
              </div>
              <div data-id="element-960">
                <label htmlFor="interests" className="block text-gray-700 font-medium mb-2" data-id="element-961">
                  Agricultural Interests
                </label>
                <select id="interests" className="input-field" defaultValue="" data-id="element-962">
                  <option value="" disabled data-id="element-963">
                    Select your primary interest
                  </option>
                  <option value="crops" data-id="element-964">Crop Farming</option>
                  <option value="livestock" data-id="element-965">Livestock</option>
                  <option value="organic" data-id="element-966">Organic Farming</option>
                  <option value="sustainable" data-id="element-967">Sustainable Agriculture</option>
                  <option value="tech" data-id="element-968">Agricultural Technology</option>
                  <option value="other" data-id="element-969">Other</option>
                </select>
              </div>
            </form>
          </div>;
      case 3:
        return <div className="w-full max-w-xl mx-auto text-center" data-id="element-970">
            <div className="bg-green-100 rounded-full p-6 inline-flex mb-6" data-id="element-971">
              <CheckIcon size={48} className="text-leaf-green" data-id="element-972" />
            </div>
            <h2 className="text-2xl font-bold mb-4" data-id="element-973">You're All Set!</h2>
            <p className="text-gray-600 mb-8" data-id="element-974">
              Your profile has been created successfully. You can now start
              exploring agricultural projects and connecting with farmers and
              investors.
            </p>
            <div className="space-y-4" data-id="element-975">
              <p className="font-medium" data-id="element-976">Here's what you can do next:</p>
              <ul className="text-left space-y-3" data-id="element-977">
                {['Browse and discover agricultural projects', 'Connect with farmers and investors', 'Track progress of your favorite projects', 'Complete your detailed profile to improve matches'].map((item, index) => <li key={index} className="flex items-center" data-id="element-978">
                    <div className="bg-leaf-green/20 rounded-full p-1 mr-3" data-id="element-979">
                      <CheckIcon size={16} className="text-leaf-green" data-id="element-980" />
                    </div>
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <Layout data-id="element-981">
      <div className="w-full min-h-[calc(100vh-8rem)] p-4 md:p-8 flex flex-col items-center justify-center" data-id="element-982">
        <div className="w-full max-w-3xl bg-white rounded-xl shadow-lg p-6 md:p-8" data-id="element-983">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8" data-id="element-984">
            {[1, 2, 3].map(stepNumber => <Fragment key={stepNumber} data-id="element-985">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${stepNumber <= step ? 'bg-leaf-green text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-986">
                  {stepNumber < step ? <CheckIcon size={20} data-id="element-987" /> : stepNumber}
                </div>
                {stepNumber < 3 && <div className={`w-16 h-1 ${stepNumber < step ? 'bg-leaf-green' : 'bg-gray-200'}`} data-id="element-988" />}
              </Fragment>)}
          </div>
          {/* Step Content */}
          {renderStepContent()}
          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8" data-id="element-989">
            {step > 1 ? <button onClick={() => setStep(step - 1)} className="btn btn-outline" data-id="element-990">
                  Back
                </button> : <div data-id="element-991"></div> // Empty div for spacing
          }
            <button onClick={handleContinue} className="btn btn-primary flex items-center" disabled={step === 1 && !userType} data-id="element-992">
              {step === 3 ? 'Get Started' : 'Continue'}
              <ChevronRightIcon size={20} className="ml-1" data-id="element-993" />
            </button>
          </div>
        </div>
      </div>
    </Layout>;
};